import { ComponentEntry } from './types';

export const CATALOG: ComponentEntry[] = [
  // ─── Layout ───────────────────────────────────────────────────────────────
  {
    type: 'Column',
    category: 'layout',
    surfaces: ['a2ui', 'space'],
    role: 'primitive',
    description: 'Vertical flex container for stacking child components.',
    props: {
      children: {
        type: 'string[]',
        description: 'Array of child component IDs to render vertically.',
        required: true,
      },
      gap: { type: 'string', description: 'CSS gap between children (e.g. "8px", "16px").' },
      padding: { type: 'string', description: 'CSS padding around the column.' },
    },
  },
  {
    type: 'Row',
    category: 'layout',
    surfaces: ['a2ui', 'space'],
    role: 'primitive',
    description: 'Horizontal flex container for arranging child components side by side.',
    props: {
      children: {
        type: 'string[]',
        description: 'Array of child component IDs to render horizontally.',
        required: true,
      },
      gap: { type: 'string', description: 'CSS gap between children.' },
      alignment: {
        type: 'string',
        description: 'Horizontal alignment of children.',
        enum: ['start', 'center', 'end', 'space-between'],
      },
    },
  },
  {
    type: 'Card',
    category: 'layout',
    surfaces: ['a2ui', 'space'],
    role: 'primitive',
    description: 'Styled container with an optional title. Use for grouping related content.',
    props: {
      title: { type: 'BoundValue', description: 'Optional card title.' },
      children: {
        type: 'string[]',
        description: 'Array of child component IDs.',
        required: true,
      },
    },
  },
  {
    type: 'Tabs',
    category: 'layout',
    surfaces: ['a2ui', 'space'],
    role: 'primitive',
    description: 'Tabbed container. Each tab has a label and its own set of child components.',
    props: {
      tabs: {
        type: 'Array<{ label: BoundValue, children: string[] }>',
        description: 'Array of tab definitions with label and child IDs.',
        required: true,
      },
    },
  },
  {
    type: 'Modal',
    category: 'layout',
    surfaces: ['a2ui', 'space'],
    role: 'primitive',
    description: 'Dialog overlay that appears above the page content.',
    props: {
      title: { type: 'BoundValue', description: 'Modal title text.', required: true },
      visible: {
        type: 'BoundValue',
        description: 'Boolean controlling modal visibility.',
        required: true,
      },
      children: {
        type: 'string[]',
        description: 'Array of child component IDs.',
        required: true,
      },
      onClose: { type: 'string', description: 'Action name dispatched when modal is closed.' },
    },
  },
  {
    type: 'List',
    category: 'layout',
    surfaces: ['a2ui', 'space'],
    role: 'primitive',
    description:
      'Data-driven repeating list. Renders a template component for each item in the data array.',
    props: {
      items: {
        type: 'BoundValue',
        description: 'Path to an array in the data model.',
        required: true,
      },
      template: {
        type: 'ComponentEntry',
        description: 'A component definition rendered for each item.',
        required: true,
      },
      emptyMessage: { type: 'BoundValue', description: 'Text shown when the items array is empty.' },
    },
  },

  // ─── Display ──────────────────────────────────────────────────────────────
  {
    type: 'Text',
    category: 'display',
    surfaces: ['a2ui', 'space'],
    role: 'primitive',
    description:
      'Text display component with semantic rendering hints for headings, body, captions, and code.',
    props: {
      content: { type: 'BoundValue', description: 'The text content to display.', required: true },
      usageHint: {
        type: 'string',
        description: 'Semantic hint controlling text rendering style.',
        enum: ['heading1', 'heading2', 'heading3', 'heading4', 'heading5', 'body', 'caption', 'code'],
      },
    },
  },
  {
    type: 'Image',
    category: 'display',
    surfaces: ['a2ui', 'space'],
    role: 'primitive',
    description: 'Image element with bound src and alt text.',
    props: {
      src: { type: 'BoundValue', description: 'Image source URL.', required: true },
      alt: { type: 'BoundValue', description: 'Alternative text for accessibility.' },
      width: { type: 'string', description: 'CSS width (e.g. "200px", "100%").' },
      height: { type: 'string', description: 'CSS height.' },
    },
  },
  {
    type: 'Icon',
    category: 'display',
    surfaces: ['a2ui', 'space'],
    role: 'primitive',
    description: 'PrimeIcons icon element. Use icon names without the "pi-" prefix.',
    props: {
      name: {
        type: 'string',
        description: 'Icon name without "pi-" prefix (e.g. "check", "times", "star").',
        required: true,
      },
      size: { type: 'string', description: 'CSS font-size (e.g. "1.5rem", "24px").' },
    },
  },
  {
    type: 'Divider',
    category: 'display',
    surfaces: ['a2ui', 'space'],
    role: 'primitive',
    description: 'Visual separator line between content sections.',
    props: {
      layout: {
        type: 'string',
        description: 'Orientation of the divider.',
        enum: ['horizontal', 'vertical'],
      },
    },
  },
  {
    type: 'ProgressBar',
    category: 'display',
    surfaces: ['a2ui', 'space'],
    role: 'primitive',
    description: 'Progress indicator showing completion percentage. Wraps PrimeReact ProgressBar.',
    props: {
      value: {
        type: 'BoundValue',
        description: 'Numeric value 0–100 representing completion percentage.',
        required: true,
      },
      label: { type: 'BoundValue', description: 'Optional label text displayed above the progress bar.' },
      showValue: {
        type: 'boolean',
        description: 'Whether to show the numeric percentage inside the bar. Defaults to true.',
      },
      color: {
        type: 'string',
        description: 'Color variant for the progress bar fill.',
        enum: ['primary', 'success', 'warning', 'danger', 'info'],
      },
      size: {
        type: 'string',
        description: 'Height of the progress bar.',
        enum: ['small', 'normal', 'large'],
      },
    },
  },
  {
    type: 'Badge',
    category: 'display',
    surfaces: ['a2ui', 'space'],
    role: 'primitive',
    description: 'Status badge / tag label. Wraps PrimeReact Tag component.',
    props: {
      label: { type: 'BoundValue', description: 'Badge text.', required: true },
      severity: {
        type: 'string',
        description: 'Visual severity / color scheme.',
        enum: ['primary', 'success', 'warning', 'danger', 'info', 'secondary'],
      },
      icon: {
        type: 'string',
        description: 'PrimeIcons icon name without "pi-" prefix (e.g. "check", "exclamation-triangle").',
      },
      size: {
        type: 'string',
        description: 'Badge size.',
        enum: ['normal', 'small'],
      },
    },
  },

  // ─── Interactive ──────────────────────────────────────────────────────────
  {
    type: 'Button',
    category: 'interactive',
    surfaces: ['a2ui', 'space'],
    role: 'primitive',
    description: 'Clickable button that dispatches a named action. Maps to PrimeReact Button.',
    props: {
      label: { type: 'BoundValue', description: 'Button text.', required: true },
      onAction: { type: 'string', description: 'Action name dispatched on click.' },
      actionContext: {
        type: 'BoundValue',
        description:
          'Explicitly bound payload sent with the dispatched action. Omit to send no payload — the button never sends the surface data model implicitly (action-context minimization).',
      },
      variant: {
        type: 'string',
        description: 'Visual style variant.',
        enum: ['primary', 'secondary', 'text', 'danger'],
      },
      icon: { type: 'string', description: 'PrimeIcons icon name (without "pi-" prefix).' },
      disabled: { type: 'boolean', description: 'Whether the button is disabled.' },
    },
  },
  {
    type: 'TextField',
    category: 'interactive',
    surfaces: ['a2ui', 'space'],
    role: 'primitive',
    description: 'Text input field with multiple variants for different data types.',
    props: {
      label: { type: 'BoundValue', description: 'Field label text.', required: true },
      value: { type: 'BoundValue', description: 'Current field value.', required: true },
      onValueChanged: { type: 'string', description: 'Action name dispatched when value changes.' },
      variant: {
        type: 'string',
        description: 'Input variant controlling the rendered input type.',
        enum: ['shortText', 'longText', 'date', 'number', 'email'],
      },
      placeholder: { type: 'BoundValue', description: 'Placeholder text.' },
      required: { type: 'boolean', description: 'Whether the field is required.' },
      disabled: { type: 'boolean', description: 'Whether the field is disabled.' },
    },
  },
  {
    type: 'CheckBox',
    category: 'interactive',
    surfaces: ['a2ui', 'space'],
    role: 'primitive',
    description: 'Toggle checkbox with label.',
    props: {
      label: { type: 'BoundValue', description: 'Checkbox label text.', required: true },
      checked: { type: 'BoundValue', description: 'Boolean checked state.', required: true },
      onToggled: { type: 'string', description: 'Action name dispatched when toggled.' },
      disabled: { type: 'boolean', description: 'Whether the checkbox is disabled.' },
    },
  },
  {
    type: 'Dropdown',
    category: 'interactive',
    surfaces: ['a2ui', 'space'],
    role: 'primitive',
    description: 'Select dropdown with options from the data model.',
    props: {
      label: { type: 'BoundValue', description: 'Dropdown label text.', required: true },
      value: { type: 'BoundValue', description: 'Currently selected value.', required: true },
      options: { type: 'BoundValue', description: 'Path to options array in data model.', required: true },
      optionLabel: {
        type: 'string',
        description: 'Field name in each option object to use as display text.',
      },
      optionValue: {
        type: 'string',
        description: 'Field name in each option object to use as the value.',
      },
      onValueChanged: {
        type: 'string',
        description: 'Action name dispatched when selection changes.',
      },
      placeholder: { type: 'BoundValue', description: 'Placeholder text.' },
      disabled: { type: 'boolean', description: 'Whether the dropdown is disabled.' },
    },
  },
  {
    type: 'NumberSlider',
    category: 'interactive',
    surfaces: ['a2ui', 'space'],
    role: 'primitive',
    description:
      'Numeric slider input with optional label and value display. Wraps PrimeReact Slider.',
    props: {
      value: { type: 'BoundValue', description: 'Current numeric value.', required: true },
      label: { type: 'BoundValue', description: 'Optional label text displayed above the slider.' },
      min: { type: 'number', description: 'Minimum value. Defaults to 0.' },
      max: { type: 'number', description: 'Maximum value. Defaults to 100.' },
      step: { type: 'number', description: 'Step increment. Defaults to 1.' },
      onValueChanged: {
        type: 'string',
        description: 'Action name dispatched when the slider value changes.',
      },
      showValue: { type: 'boolean', description: 'Whether to display the current numeric value. Defaults to true.' },
      disabled: { type: 'boolean', description: 'Whether the slider is disabled.' },
    },
  },

  // ─── Domain ───────────────────────────────────────────────────────────────
  {
    type: 'SphereTypeSelector',
    category: 'domain',
    surfaces: ['a2ui', 'space'],
    role: 'primitive',
    description: 'Schema type picker with sphere type avatars. Center Platform specific.',
    props: {
      value: { type: 'BoundValue', description: 'Currently selected schema value.', required: true },
      schemas: { type: 'BoundValue', description: 'Path to schema list in data model.', required: true },
      onValueChanged: { type: 'string', description: 'Action name dispatched when selection changes.' },
    },
  },
  {
    type: 'SphereCard',
    category: 'domain',
    surfaces: ['a2ui', 'space'],
    role: 'primitive',
    description:
      'Sphere summary card showing type avatar, name, and status badge. Center Platform specific.',
    props: {
      sphere: {
        type: 'BoundValue',
        description:
          'Path to sphere data object (expects name, schemaSystemName, status fields).',
        required: true,
      },
      onAction: { type: 'string', description: 'Action name dispatched when the card is clicked.' },
    },
  },
  {
    type: 'UrgencySelector',
    category: 'domain',
    surfaces: ['a2ui', 'space'],
    role: 'primitive',
    description: 'Visual urgency level picker (1–5 with colored indicators).',
    props: {
      label: { type: 'BoundValue', description: 'Optional label, defaults to "Urgency".' },
      value: { type: 'BoundValue', description: 'Numeric 1–5, defaults to 3.' },
      onValueChanged: { type: 'string', description: 'Action name dispatched on change.' },
      disabled: { type: 'boolean', description: 'Whether the selector is disabled.' },
    },
  },
  {
    type: 'PerspectiveDisplay',
    category: 'domain',
    surfaces: ['a2ui', 'space'],
    role: 'primitive',
    description: 'Shows a perspective assignment (parent, owner, etc.).',
    props: {
      label: { type: 'BoundValue', description: 'Display label, e.g. "Parent" or "Owner".' },
      perspectiveName: { type: 'BoundValue', description: 'Perspective system name.' },
      assigneeName: { type: 'BoundValue', description: 'Name of the assigned sphere/user.' },
      assigneeId: { type: 'BoundValue', description: 'ID of the assigned sphere/user.' },
      required: { type: 'boolean', description: 'Shows "Required" badge when true.' },
    },
  },
  {
    type: 'SphereAvatar',
    category: 'domain',
    surfaces: ['a2ui', 'space'],
    role: 'primitive',
    description: 'Sphere avatar image/initials display.',
    props: {
      sphereId: { type: 'BoundValue', description: 'Sphere ID for thumbnail fetch.' },
      sphereName: { type: 'BoundValue', description: 'Name for initials fallback.' },
      avatar: { type: 'BoundValue', description: 'Avatar string override.' },
      size: {
        type: 'string',
        description: 'Display size.',
        enum: ['xsmall', 'small', 'medium', 'large', 'xlarge'],
      },
      shape: {
        type: 'string',
        description: 'Shape of the avatar.',
        enum: ['circle', 'square'],
      },
      status: {
        type: 'BoundValue',
        description: 'Sphere status (on_track, at_risk, critical, etc.).',
      },
    },
  },
  {
    type: 'SphereEditor',
    category: 'domain',
    surfaces: ['a2ui', 'space'],
    role: 'section',
    description: 'Auto-generated editable form driven by an initialized sphere and its schema.',
    props: {
      sphere: {
        type: 'BoundValue',
        description:
          'Path to the initialized sphere object from initialize_sphere (includes id, label, avatar, properties, assignedPerspectives).',
        required: true,
      },
      schema: {
        type: 'BoundValue',
        description:
          'Path to the schema definition from get_schema/list_schemas (includes properties and perspectives).',
        required: true,
      },
      title: { type: 'BoundValue', description: 'Optional editor heading.' },
      onSave: {
        type: 'string',
        description:
          'Action name dispatched with context: { sphereId, schemaSystemName, ...fieldValues }.',
      },
      onCancel: { type: 'string', description: 'Optional action name for cancel.' },
      editable: { type: 'boolean', description: 'Whether the form is editable. Defaults to true.' },
    },
    whenToUse:
      '**Preferred over manual form construction.** Use it by:\n' +
      '1. Calling `initialize_sphere` (for new spheres) or `get_sphere` (for existing) to get sphere data\n' +
      '2. Calling `get_schema` to fetch the schema definition\n' +
      '3. Pushing both via `dataModelUpdate` (e.g. key "sphere" and key "schema")\n' +
      '4. Emitting a single `SphereEditor` component referencing those data paths',
  },

  // ─── Metaspace Organisms ──────────────────────────────────────────────────
  {
    type: 'Atlas',
    category: 'organism',
    surfaces: ['metaspace', 'space'],
    role: 'surface',
    description:
      'Configurable sphere dashboard driven by an atlas_config template. The primary metaspace organism for displaying and interacting with sphere data.',
    props: {
      atlasConfigRef: {
        type: 'string',
        description:
          'system_name of the atlas_config sphere whose template definition drives the Atlas layout.',
        required: true,
      },
      sphereIdSource: {
        type: 'string',
        description:
          'How to resolve the sphere displayed in Atlas. Omit to use the current sphere from context.',
        enum: ['identity', 'organization'],
      },
      sphereId: {
        type: 'string',
        description:
          'Literal sphere ID override. Used only when sphereIdSource is absent and you need a fixed sphere.',
      },
      gridPosition: {
        type: '{ x: number; y: number; width: number; height: number }',
        description: 'Grid cell position (x, y) and size (width, height) in grid units.',
        required: true,
      },
      settings: {
        type: '{ collapsible?: boolean; draggable?: boolean }',
        description: 'Optional organism-level collapsible and draggable overrides.',
      },
    },
    dependencies:
      '`atlasConfigRef` must reference a pre-existing `atlas_config` sphere by system_name. ' +
      'The atlas_config sphere must be deployed to the organization before the Atlas organism can render.',
    whenToUse:
      'Use Atlas when a metaspace needs to display and interact with sphere data driven by a reusable template. ' +
      'Set `atlasConfigRef` to the system_name of the atlas_config sphere. ' +
      'Set `sphereIdSource: "identity"` for personal spheres or `"organization"` for org-level spheres; ' +
      'omit to default to the current sphere in context.',
    examples: [
      '{ type: "Atlas", atlasConfigRef: "my_dashboard", gridPosition: { x: 0, y: 0, width: 8, height: 6 } }',
      '{ type: "Atlas", atlasConfigRef: "identity_profile", sphereIdSource: "identity", gridPosition: { x: 0, y: 0, width: 12, height: 8 } }',
    ],
  },
  {
    type: 'ArmillaryView',
    category: 'organism',
    surfaces: ['metaspace', 'space'],
    role: 'surface',
    description:
      'Orbital sphere-graph visualization. Renders the current sphere and its immediate hierarchy as concentric rings. Enables chat context via the metaspace toolbar.',
    props: {
      centerImagePath: {
        type: 'string',
        description: 'Optional custom center image URL displayed at the orbital core.',
      },
      gridPosition: {
        type: '{ x: number; y: number; width: number; height: number }',
        description: 'Grid cell position (x, y) and size (width, height) in grid units.',
        required: true,
      },
      settings: {
        type: '{ collapsible?: boolean; draggable?: boolean }',
        description: 'Optional organism-level collapsible and draggable overrides.',
      },
    },
    whenToUse:
      'Use ArmillaryView to visualize a sphere\'s immediate hierarchy in an orbital style. ' +
      'Always binds to the current sphere from context — no sphereId configuration needed. ' +
      'Including ArmillaryView activates the metaspace toolbar (chat, Focus/tasks, Stream, Trajectory) automatically.',
    examples: [
      '{ type: "ArmillaryView", gridPosition: { x: 0, y: 0, width: 12, height: 8 } }',
    ],
  },
  {
    type: 'ArmillaryActions',
    category: 'organism',
    surfaces: ['metaspace', 'space'],
    role: 'section',
    description:
      'Quick-action panel for the Armillary orbital context. Displays sphere-level actions for the current sphere. Auto-hides when the metaspace toolbar is open.',
    props: {
      gridPosition: {
        type: '{ x: number; y: number; width: number; height: number }',
        description: 'Grid cell position (x, y) and size (width, height) in grid units.',
        required: true,
      },
      settings: {
        type: '{ collapsible?: boolean; draggable?: boolean }',
        description: 'Optional organism-level collapsible and draggable overrides.',
      },
    },
    whenToUse:
      'Pair ArmillaryActions with ArmillaryView to provide shortcut actions in an orbital workspace. ' +
      'Position it near ArmillaryView in the grid. The organism automatically hides when the toolbar opens.',
    examples: [
      '{ type: "ArmillaryActions", gridPosition: { x: 0, y: 8, width: 12, height: 2 } }',
    ],
  },
  {
    type: 'Users',
    category: 'organism',
    surfaces: ['metaspace', 'space'],
    role: 'surface',
    description:
      'Organization user directory. Lists all members in the current organization.',
    props: {
      gridPosition: {
        type: '{ x: number; y: number; width: number; height: number }',
        description: 'Grid cell position (x, y) and size (width, height) in grid units.',
        required: true,
      },
      settings: {
        type: '{ collapsible?: boolean; draggable?: boolean }',
        description: 'Optional organism-level collapsible and draggable overrides.',
      },
    },
    whenToUse:
      'Use in organization-level metaspaces (e.g., an org home template) to display the member directory. ' +
      'Resolves the organizationId automatically from the session context — no configuration needed.',
    examples: [
      '{ type: "Users", gridPosition: { x: 0, y: 0, width: 6, height: 8 } }',
    ],
  },
  {
    type: 'Profile',
    category: 'organism',
    surfaces: ['metaspace', 'space'],
    role: 'surface',
    description:
      'Current user identity profile display. Shows the logged-in user\'s profile data including name, avatar, and identity attributes.',
    props: {
      gridPosition: {
        type: '{ x: number; y: number; width: number; height: number }',
        description: 'Grid cell position (x, y) and size (width, height) in grid units.',
        required: true,
      },
      settings: {
        type: '{ collapsible?: boolean; draggable?: boolean }',
        description: 'Optional organism-level collapsible and draggable overrides.',
      },
    },
    whenToUse:
      'Use in personal or identity-level metaspaces. Resolves identityId and organizationId automatically from the user session.',
    examples: [
      '{ type: "Profile", gridPosition: { x: 0, y: 0, width: 6, height: 6 } }',
    ],
  },
  {
    type: 'Preferences',
    category: 'organism',
    surfaces: ['metaspace', 'space'],
    role: 'section',
    description:
      'User preferences panel. Allows the current user to manage notification and display settings.',
    props: {
      gridPosition: {
        type: '{ x: number; y: number; width: number; height: number }',
        description: 'Grid cell position (x, y) and size (width, height) in grid units.',
        required: true,
      },
      settings: {
        type: '{ collapsible?: boolean; draggable?: boolean }',
        description: 'Optional organism-level collapsible and draggable overrides.',
      },
    },
    whenToUse:
      'Use alongside Profile in personal or identity-level metaspaces. Resolves identityId automatically from the user session.',
    examples: [
      '{ type: "Preferences", gridPosition: { x: 6, y: 0, width: 6, height: 6 } }',
    ],
  },
  {
    type: 'TreeMap',
    category: 'organism',
    surfaces: ['metaspace', 'space'],
    role: 'surface',
    description:
      'Hierarchical sphere-tree visualization with configurable context template and filtering. Renders child spheres in a navigable card grid.',
    props: {
      'config.contextSystemName': {
        type: 'string',
        description:
          'system_name of the context (view template) that controls how each sphere card renders in the tree.',
      },
      'config.rootSphereId': {
        type: 'string',
        description: 'Sphere ID to use as the tree root. Defaults to the current sphere from context.',
      },
      'config.viewSystemName': {
        type: 'string',
        description: 'Optional system_name of a view to apply to the tree listing.',
      },
      'config.editable': {
        type: 'boolean',
        description: 'Whether spheres in the tree can be edited inline. Defaults to true.',
      },
      'config.initialCardView': {
        type: 'string',
        description: 'Initial card display mode.',
        enum: ['standard'],
      },
      'config.showBreadcrumbs': {
        type: 'boolean',
        description: 'Whether to show breadcrumb navigation. Defaults to true.',
      },
      'config.showFilters': {
        type: 'boolean',
        description: 'Whether to show filter controls above the tree. Defaults to true.',
      },
      'config.showAI': {
        type: 'boolean',
        description: 'Whether to show AI suggestions alongside the tree. Defaults to false.',
      },
      'config.defaultPageSize': {
        type: 'number',
        description: 'Default number of spheres per page. Defaults to 10.',
      },
      'config.useGridLayout': {
        type: 'boolean',
        description: 'Whether to display spheres as a grid (true) or list (false). Defaults to true.',
      },
      gridPosition: {
        type: '{ x: number; y: number; width: number; height: number }',
        description: 'Grid cell position (x, y) and size (width, height) in grid units.',
        required: true,
      },
      settings: {
        type: '{ collapsible?: boolean; draggable?: boolean }',
        description: 'Optional organism-level collapsible and draggable overrides.',
      },
    },
    dependencies:
      '`config.contextSystemName` must reference a pre-existing context by system_name in the organization. ' +
      'If omitted, spheres render with their default display.',
    whenToUse:
      'Use in sphere-type metaspaces to display and navigate child sphere hierarchies. ' +
      'Set `config.contextSystemName` to control how sphere cards render — this is the key configuration knob.',
    examples: [
      '{ type: "TreeMap", config: { contextSystemName: "project_context", showAI: false }, gridPosition: { x: 0, y: 0, width: 12, height: 8 } }',
    ],
  },
  {
    type: 'task-list',
    category: 'organism',
    surfaces: ['metaspace', 'space'],
    role: 'section',
    description:
      'Unified task list showing tasks assigned to or owned by a configurable sphere context (user, identity, organization, or current sphere).',
    props: {
      sphereIdSource: {
        type: 'string',
        description:
          'Determines which sphere context to fetch tasks for. Defaults to the user sphere ("my tasks").',
        enum: ['identity', 'user', 'organization', 'current', 'sphere'],
      },
      title: {
        type: 'string',
        description: 'Optional panel title. Defaults to the localized "My Tasks" string.',
      },
      'taskListSettings.maxRows': {
        type: 'number',
        description: 'Maximum number of task rows to display.',
      },
      'taskListSettings.showPriority': {
        type: 'boolean',
        description: 'Whether to show the priority column.',
      },
      'taskListSettings.showAssignee': {
        type: 'boolean',
        description: 'Whether to show the assignee column.',
      },
      'taskListSettings.showDueDate': {
        type: 'boolean',
        description: 'Whether to show the due date column.',
      },
      'taskListSettings.enableInlineActions': {
        type: 'boolean',
        description: 'Whether to enable inline task actions (complete, edit). Defaults to true.',
      },
      'taskListSettings.refreshInterval': {
        type: 'number',
        description: 'Polling interval in milliseconds for automatic task refresh.',
      },
      'taskListSettings.filterByStatus': {
        type: 'string[]',
        description: 'Filter tasks to only show these status codes.',
      },
      'taskListSettings.filterByTaskType': {
        type: 'string[]',
        description: 'Filter tasks by task type.',
      },
      'taskListSettings.filterByCheckInStatus': {
        type: 'string[]',
        description: 'Filter tasks by check-in status.',
      },
      'taskListSettings.includeVirtual': {
        type: 'boolean',
        description: 'Whether to include virtual (synthesized) tasks.',
      },
      'taskListSettings.lookaheadDays': {
        type: 'number',
        description: 'Number of future days to include in the task lookahead window.',
      },
      'taskListSettings.sortBy': {
        type: 'string',
        description: 'Field name to sort tasks by.',
      },
      'taskListSettings.sortOrder': {
        type: 'string',
        description: 'Sort direction.',
        enum: ['asc', 'desc'],
      },
      gridPosition: {
        type: '{ x: number; y: number; width: number; height: number }',
        description: 'Grid cell position (x, y) and size (width, height) in grid units.',
        required: true,
      },
      'settings.collapsible': {
        type: 'boolean',
        description: 'Whether the panel is collapsible. Defaults to true.',
      },
    },
    whenToUse:
      'Use in personal or sphere-context metaspaces to surface actionable tasks. ' +
      'The default `sphereIdSource: "user"` shows tasks assigned to the current user ("my tasks"). ' +
      'Use `"identity"` for identity-sphere tasks, `"current"` or `"sphere"` for the current sphere\'s tasks, ' +
      '`"organization"` for org-wide task views.',
    examples: [
      '{ type: "task-list", sphereIdSource: "user", gridPosition: { x: 0, y: 6, width: 12, height: 4 } }',
      '{ type: "task-list", sphereIdSource: "current", title: "Sphere Tasks", taskListSettings: { showDueDate: true, sortBy: "dueDate", sortOrder: "asc" }, gridPosition: { x: 6, y: 0, width: 6, height: 6 } }',
    ],
  },
  {
    type: 'Scorecard',
    category: 'organism',
    surfaces: ['metaspace', 'space'],
    role: 'section',
    description:
      'Status/velocity/gravity rollup table for a context — one row per sphere in scope. Used by governance-orbit-style approaches (e.g. a monthly ops review) to show data freshness at a glance.',
    props: {
      sphereIdSource: {
        type: 'string',
        description:
          'Determines which sphere the scorecard\'s context is rooted at — typically the orbit or rotation the scorecard belongs to. Defaults to the current sphere.',
        enum: ['identity', 'organization', 'current', 'sphere'],
      },
      'scorecardSettings.viewSystemName': {
        type: 'string',
        description: 'system_name of the scorecard context view (viewType: "scorecard") that supplies the context and surfaceColumns config.',
        required: true,
      },
      'scorecardSettings.sphereId': {
        type: 'string',
        description: 'Literal sphere ID override, used when sphereIdSource is omitted.',
      },
      gridPosition: {
        type: '{ x: number; y: number; width: number; height: number }',
        description: 'Grid cell position (x, y) and size (width, height) in grid units.',
        required: true,
      },
    },
    dependencies:
      '`scorecardSettings.viewSystemName` must reference a pre-existing context view with `viewType: "scorecard"`. ' +
      'The referenced context should be executed with status enrichment for the status/velocity/gravity columns to populate.',
    whenToUse:
      'Use in orbit/rotation-scoped metaspaces to surface a data-freshness or health rollup for the spheres a context selects. ' +
      'Set `sphereIdSource: "current"` (or `"sphere"`) to scope the scorecard to the metaspace\'s own sphere — the common case for a meeting/review surface.',
    examples: [
      '{ type: "Scorecard", sphereIdSource: "current", scorecardSettings: { viewSystemName: "monthly_ops_review_scorecard" }, gridPosition: { x: 0, y: 0, width: 12, height: 8 } }',
    ],
  },

  // ─── Space Surface Primitives ──────────────────────────────────────────────
  {
    type: 'SpaceGrid',
    category: 'layout',
    surfaces: ['space'],
    role: 'surface',
    internal: true,
    description:
      'Uniform-layout Surface root for a Space document. Arranges child Sections in a responsive grid, mirroring the legacy metaspace grid layout.',
    props: {
      sections: {
        type: 'string[]',
        description: 'Array of child Section component IDs to render in the grid.',
        required: true,
      },
      columns: { type: 'number', description: 'Number of grid columns. Defaults to 12.' },
      rowHeight: { type: 'number', description: 'Height in pixels of one grid row unit.' },
    },
    whenToUse:
      'Use as the Surface root when `surface.layout` is `"uniform"` — the direct analogue of the ' +
      'legacy metaspace grid. Prefer `ContextShell` when the Space needs a fixed primary region ' +
      'plus docked Sections (`surface.layout: "primary"`).',
  },
  {
    type: 'Section',
    category: 'layout',
    surfaces: ['space'],
    role: 'section',
    internal: true,
    description:
      'A named region of a Space Surface. Wraps one child component subtree (which may be a ' +
      'composition reference) and carries its own grid position.',
    props: {
      title: { type: 'BoundValue', description: 'Optional Section heading.' },
      child: {
        type: 'string',
        description: 'id of the single child component rendered inside the Section.',
        required: true,
      },
      gridPosition: {
        type: '{ x: number; y: number; width: number; height: number }',
        description: 'Grid cell position (x, y) and size (width, height) in grid units.',
        required: true,
      },
    },
  },
  {
    type: 'ContextShell',
    category: 'layout',
    surfaces: ['space'],
    role: 'surface',
    internal: true,
    description:
      'Primary-layout Surface root for a Space document. Docks one primary child region plus an ' +
      'ordered list of secondary Sections, mirroring an Armillary-style fixed-plus-docked layout.',
    props: {
      primary: {
        type: 'string',
        description: 'id of the component rendered in the primary region.',
        required: true,
      },
      sections: {
        type: 'string[]',
        description: 'Array of child Section component IDs docked around the primary region.',
      },
    },
    whenToUse:
      'Use as the Surface root when `surface.layout` is `"primary"`. Prefer `SpaceGrid` for a ' +
      'plain uniform grid with no distinguished primary region.',
  },
  {
    type: 'Chart',
    category: 'display',
    surfaces: ['space'],
    role: 'primitive',
    description:
      'Data-bound chart. Renders a series drawn from the data model as one of the supported chart kinds.',
    props: {
      kind: {
        type: 'string',
        description: 'Chart rendering kind.',
        enum: ['sparkline', 'line', 'bar'],
        required: true,
      },
      series: {
        type: 'BoundValue',
        description: 'Path to the series data (array of numeric points) in the data model.',
        required: true,
      },
      label: { type: 'BoundValue', description: 'Optional chart label.' },
      color: { type: 'string', description: 'CSS variable or color token for the series stroke/fill.' },
    },
    dependencies: 'Renders via the existing `StatusSparklineAtom` for `kind: "sparkline"`.',
  },

  // ─── Renderer primitives (EAINT-855) ───────────────────────────────────────
  // Internal: renderer plumbing for `resolveRenderer`'s per-data-type default table
  // (component-catalog `typeDefaults.ts`), not gallery/rail/search placement content.
  {
    type: 'BooleanField',
    category: 'interactive',
    surfaces: ['space'],
    role: 'primitive',
    internal: true,
    description:
      'Boolean toggle for a schema property. Wraps SwitchAtom and speaks the property-render contract (value/onValueChanged), unlike the older CheckBox primitive (checked/onToggled).',
    props: {
      value: { type: 'BoundValue', description: 'Current boolean value.', required: true },
      label: { type: 'BoundValue', description: 'Field label text.' },
      onValueChanged: { type: 'string', description: 'Action name dispatched when value changes.' },
      disabled: { type: 'boolean', description: 'Whether the toggle is disabled.' },
    },
  },
  {
    type: 'NumberField',
    category: 'interactive',
    surfaces: ['space'],
    role: 'primitive',
    internal: true,
    description: 'Numeric input field with locale/currency-aware formatting. Wraps InputNumberAtom.',
    props: {
      value: { type: 'BoundValue', description: 'Current numeric value.', required: true },
      label: { type: 'BoundValue', description: 'Field label text.' },
      onValueChanged: { type: 'string', description: 'Action name dispatched when value changes.' },
      numberType: {
        type: 'string',
        description: 'Numeric formatting mode.',
        enum: ['decimal', 'currency', 'integer'],
      },
      currency: { type: 'string', description: 'ISO-4217 currency code (only used when numberType is "currency").' },
      min: { type: 'number', description: 'Minimum value.' },
      max: { type: 'number', description: 'Maximum value.' },
      step: { type: 'number', description: 'Step increment.' },
      precision: { type: 'number', description: 'Maximum decimal places.' },
      placeholder: { type: 'BoundValue', description: 'Placeholder text.' },
      disabled: { type: 'boolean', description: 'Whether the field is disabled.' },
    },
  },
  {
    type: 'DateField',
    category: 'interactive',
    surfaces: ['space'],
    role: 'primitive',
    internal: true,
    description: 'Date-only picker input. Wraps DatePickerAtom.',
    props: {
      value: { type: 'BoundValue', description: 'Current ISO date value.', required: true },
      label: { type: 'BoundValue', description: 'Field label text.' },
      onValueChanged: { type: 'string', description: 'Action name dispatched when value changes.' },
      dateFormat: { type: 'string', description: 'PrimeReact Calendar date format string.' },
      disabled: { type: 'boolean', description: 'Whether the field is disabled.' },
    },
  },
  {
    type: 'DateTimeField',
    category: 'interactive',
    surfaces: ['space'],
    role: 'primitive',
    internal: true,
    description: 'Date-and-time picker input. Wraps DateTimePickerInputAtom.',
    props: {
      value: { type: 'BoundValue', description: 'Current ISO datetime value.', required: true },
      label: { type: 'BoundValue', description: 'Field label text.' },
      onValueChanged: { type: 'string', description: 'Action name dispatched when value changes.' },
      disabled: { type: 'boolean', description: 'Whether the field is disabled.' },
    },
  },
  {
    type: 'UnitField',
    category: 'interactive',
    surfaces: ['space'],
    role: 'primitive',
    internal: true,
    description: 'Unit-aware numeric input with a unit switcher. Wraps UnitValueInputAtom.',
    props: {
      value: { type: 'BoundValue', description: 'Current value (number, partial, or full UnitValue).', required: true },
      unitConfig: { type: 'UnitConfig', description: 'Unit category, allowed units, and display precision.', required: true },
      onValueChanged: { type: 'string', description: 'Action name dispatched when value changes.' },
      min: { type: 'number', description: 'Minimum value.' },
      max: { type: 'number', description: 'Maximum value.' },
      step: { type: 'number', description: 'Step increment.' },
      disabled: { type: 'boolean', description: 'Whether the field is disabled.' },
    },
  },
  {
    type: 'ReferenceField',
    category: 'interactive',
    surfaces: ['space'],
    role: 'primitive',
    internal: true,
    description: 'Sphere reference picker, constrained to a target schema type. Wraps SphereReferenceInputMolecule.',
    props: {
      value: { type: 'BoundValue', description: 'Current target sphere id.', required: true },
      targetSchemaSystemName: { type: 'string', description: 'system_name of the schema type candidates must match.', required: true },
      displayProperty: { type: 'string', description: 'Property of the target sphere to display. Defaults to "name".' },
      onValueChanged: { type: 'string', description: 'Action name dispatched when the selected sphere changes.' },
      disabled: { type: 'boolean', description: 'Whether the field is disabled.' },
    },
  },
  {
    type: 'TagListField',
    category: 'interactive',
    surfaces: ['space'],
    role: 'primitive',
    internal: true,
    description: 'Free-text tag/chip list editor for array-typed values. Wraps ChipsAtom.',
    props: {
      value: { type: 'BoundValue', description: 'Current array of string values.', required: true },
      onValueChanged: { type: 'string', description: 'Action name dispatched when the array changes.' },
      placeholder: { type: 'BoundValue', description: 'Placeholder text shown when empty.' },
      disabled: { type: 'boolean', description: 'Whether the field is disabled.' },
    },
  },
  {
    type: 'JsonField',
    category: 'interactive',
    surfaces: ['space'],
    role: 'primitive',
    internal: true,
    description: 'Read/write JSON textarea editor for json-typed values. Wraps TextareaAtom.',
    props: {
      value: { type: 'BoundValue', description: 'Current JSON-serializable value.', required: true },
      onValueChanged: { type: 'string', description: 'Action name dispatched with the parsed value when the text is valid JSON.' },
      disabled: { type: 'boolean', description: 'Whether the field is disabled.' },
    },
  },
  {
    type: 'DateTimeDisplay',
    category: 'display',
    surfaces: ['space'],
    role: 'primitive',
    internal: true,
    description: 'Read-only date/datetime display. Wraps DateTimeDisplayAtom.',
    props: {
      value: { type: 'BoundValue', description: 'ISO date or datetime string to display.', required: true },
      format: {
        type: 'string',
        description: 'Display format.',
        enum: ['full', 'short', 'compact', 'iso', 'custom'],
      },
      customFormat: { type: 'string', description: 'Custom date-format string (used when format is "custom").' },
      showTimezone: { type: 'boolean', description: 'Whether to show the timezone name.' },
      showIso: { type: 'boolean', description: 'Whether to show the raw ISO string.' },
    },
  },
  {
    type: 'ChipListDisplay',
    category: 'display',
    surfaces: ['space'],
    role: 'primitive',
    internal: true,
    description: 'Read-only chip list display for array-typed values. Wraps ChipListDisplayAtom.',
    props: {
      value: { type: 'BoundValue', description: 'Array of values to display as chips.', required: true },
      compact: { type: 'boolean', description: 'Whether to render in compact mode.' },
    },
  },
  {
    type: 'GenericDisplay',
    category: 'display',
    surfaces: ['space'],
    role: 'primitive',
    internal: true,
    description:
      'Terminal read-only fallback display — renders any value as text (JSON-stringifying non-primitives).',
    props: {
      value: { type: 'BoundValue', description: 'Value to display.', required: true },
    },
  },
];
